import { hasSubclassFeature } from '../../../../shared/rules/subclasses/index.mjs';
import { EMPOWERED_EVOCATION_FEATURE_ID, POTENT_CANTRIP_FEATURE_ID, empoweredEvocationBonus } from '../../../../shared/rules/subclasses/wizard/evoker.mjs';
import spells from '../../../../data/spells';

const LABEL = 'Empowered Evocation';
const ineligible = (reasonCode) => ({ status: 'ineligible', featureId: EMPOWERED_EVOCATION_FEATURE_ID, featureLabel: LABEL, reasonCode });
const wizardSpell = (spell) => spell?.source?.sourceId === 'wizard' && spell.source.sourceType === 'class';

/**
 * Empowered Evocation (Evoker 10) on a spell attack: Intelligence added to one damage roll of a Wizard Evocation spell.
 * Automatic, and claimed once per turn so a spell with several attacks adds it once.
 */
export const evaluateEmpoweredEvocation = ({ context }) => {
  if (!hasSubclassFeature(context?.attacker?.entity, EMPOWERED_EVOCATION_FEATURE_ID)) return ineligible('below-level-10');
  if (context?.roll?.hit === false) return ineligible('not-a-hit');
  const spell = context?.attack?.source;
  if (context?.attack?.kind === 'weapon' || !wizardSpell(spell) || String(spells[spell.spellId]?.school).toLowerCase() !== 'evocation') return ineligible('not-a-wizard-evocation');
  const bonus = empoweredEvocationBonus(context.attack.abilityModifier);
  if (!bonus) return ineligible('no-modifier');
  const combat = context.combat;
  if (!combat?.campaignId || !Number.isInteger(combat.turnSequence)) return ineligible('no-authoritative-turn');
  const previous = combat.turnFeatureUsage?.[context.attacker.combatantId]?.[EMPOWERED_EVOCATION_FEATURE_ID];
  if (previous?.turnSequence === combat.turnSequence && previous.resolutionId !== context.resolutionId) return ineligible('already-used-this-turn');
  return {
    status: 'automatic', featureId: EMPOWERED_EVOCATION_FEATURE_ID, featureLabel: LABEL, reasonCode: 'wizard-evocation',
    components: [{ id: EMPOWERED_EVOCATION_FEATURE_ID, label: LABEL, source: { type: 'feature', featureId: EMPOWERED_EVOCATION_FEATURE_ID }, flat: bonus, damageType: { mode: 'inherit' } }],
    claims: [{ campaignId: combat.campaignId, combatantId: context.attacker.combatantId, featureId: EMPOWERED_EVOCATION_FEATURE_ID, turnSequence: combat.turnSequence, resolutionId: context.resolutionId }],
  };
};

/**
 * Potent Cantrip (Evoker 3) on a miss: half the cantrip's damage, rolled here because a miss rolls none. It has no
 * other effect, and it's automatic, so it asks nothing.
 */
export const evaluatePotentCantrip = async ({ character, attack, outcome, rollDice }) => {
  if (outcome !== 'miss' || !hasSubclassFeature(character, POTENT_CANTRIP_FEATURE_ID) || !wizardSpell(attack?.spell) || Number(attack.spell.level) !== 0) return [];
  const match = /(\d+)d(\d+)/.exec(String(attack.damage || ''));
  if (!match) return [];
  const count = Number(match[1]), sides = Number(match[2]);
  const roll = await rollDice?.([{ count, sides }]).catch(() => null);
  const rolled = Array.isArray(roll?.rolls?.[0]) ? roll.rolls[0] : Array.from({ length: count }, () => 1 + Math.floor(Math.random() * sides));
  const damage = Math.floor(rolled.reduce((sum, value) => sum + value, 0) / 2);
  return damage > 0 ? [{ featureId: POTENT_CANTRIP_FEATURE_ID, status: 'automatic', label: 'Potent Cantrip', fixedDamage: damage, damageType: attack.damageType || '' }] : [];
};
